/**
 * Getting into buildings.
 *
 * The obvious approach — put each interior physically inside its building's
 * shell and walk in — fails here for a concrete reason: the building models
 * are solid exterior meshes with no inside, so "walking in" means walking into
 * opaque geometry. Interiors also then have to fit inside their plot, which
 * the 36 m office does not.
 *
 * So interiors live on their own coordinate ISLAND, far from the district, and
 * crossing a threshold moves the player between the two with a fade. This is a
 * standard technique and it buys three things for free:
 *
 *  - an interior can be any size, unconstrained by its plot,
 *  - interior and exterior colliders can never alias each other (the bug that
 *    let a player be blocked by a wall inside a building they weren't in),
 *  - each interior keeps its own local coordinates, so the existing office
 *    needs no changes at all.
 *
 * The cost is that you can't see the street through an interior window, which
 * for an office full of workflow data is a trade worth making.
 */

import { PLOTS, plotDoorPosition, plotFrontZ, DOOR_WIDTH, type PlotDef } from './district'
import { BOARD_HALF_Z } from './board'
import { OPS_HALF_Z } from './ops'
import { LIBRARY_HALF_Z } from './library'
import { CAMPUS_ROOMS } from './campusRooms'

/** Interiors are parked here, well clear of the district. */
export const INTERIOR_ISLAND_Z = -1000

/**
 * The BOARD interior gets its own island slot, well south of the office island
 * so their shells + backdrops can never overlap (the office spans ±18 m around
 * -1000; the board spans ±14 m around -1120).
 */
export const BOARD_ISLAND_Z = INTERIOR_ISLAND_Z - 120

export interface Threshold {
  /** Plot this threshold belongs to. */
  plotId: string
  /** Axis-aligned trigger box in world XZ. */
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

export interface InteriorDef {
  plotId: string
  /** Where the player lands inside, and the way they face. */
  spawn: { x: number; z: number; yaw: number }
  /** Where they land back on the street when they leave. */
  exit: { x: number; z: number; yaw: number }
  /** Trigger that takes the player out again. */
  exitThreshold: Threshold
}

/** Depth of the trigger volume in front of a door. */
const THRESHOLD_DEPTH = 1.4

/** Trigger volume just outside a plot's door. */
export function entryThreshold(plot: PlotDef): Threshold {
  const [doorX] = plotDoorPosition(plot)
  const front = plotFrontZ(plot)
  // Outward from the building: north plots open toward +Z, south toward -Z.
  const outward = plot.side === 'north' ? 1 : -1
  const nearZ = front
  const farZ = front + outward * THRESHOLD_DEPTH
  return {
    plotId: plot.id,
    minX: doorX - DOOR_WIDTH / 2,
    maxX: doorX + DOOR_WIDTH / 2,
    minZ: Math.min(nearZ, farZ),
    maxZ: Math.max(nearZ, farZ),
  }
}

/** Every enterable plot's entry trigger. */
export function entryThresholds(): Threshold[] {
  return PLOTS.filter((p) => p.enterable).map(entryThreshold)
}

/** Point-in-threshold test, ignoring height (all thresholds are at grade). */
export function inThreshold(t: Threshold, x: number, z: number): boolean {
  return x >= t.minX && x <= t.maxX && z >= t.minZ && z <= t.maxZ
}

/** The threshold the player is standing in, if any. */
export function thresholdAt(list: readonly Threshold[], x: number, z: number): Threshold | null {
  for (const t of list) if (inThreshold(t, x, z)) return t
  return null
}

/**
 * Where the player lands when stepping out of a building: on the pavement in
 * front of its door, facing away from the building. Pushed clear of the
 * threshold volume so leaving doesn't immediately re-trigger entering.
 */
export function streetExitFor(plot: PlotDef): { x: number; z: number; yaw: number } {
  const [doorX] = plotDoorPosition(plot)
  const front = plotFrontZ(plot)
  const outward = plot.side === 'north' ? 1 : -1
  return {
    x: doorX,
    z: front + outward * (THRESHOLD_DEPTH + 1.2),
    // Yaw 0 looks toward -Z; a north-side building faces the player toward +Z.
    yaw: plot.side === 'north' ? 180 : 0,
  }
}

/**
 * The office interior, on the island.
 *
 * It keeps its own local coordinates entirely — the island offset is applied
 * as a placement, exactly as the plot transform was. The player lands just
 * inside its south entry looking north across the desk floor.
 */
export const OFFICE_INTERIOR: InteriorDef = {
  plotId: 'P3',
  // Frame the Core and oculus across the atrium instead of aiming directly
  // into the stair underside. The entry/exit axis remains physically clear.
  spawn: { x: 0, z: INTERIOR_ISLAND_Z + 9.5, yaw: 23 },
  exit: streetExitFor(PLOTS.find((p) => p.id === 'P3')!),
  exitThreshold: {
    plotId: 'P3',
    minX: -1.2,
    maxX: 1.2,
    minZ: INTERIOR_ISLAND_Z + 10.6,
    maxZ: INTERIOR_ISLAND_Z + 12.4,
  },
}

/**
 * The BOARD interior (plot P1), on its own island slot.
 *
 * The room is 28 × 16 m (halfZ 8), so the player lands just inside its south
 * entry looking north across the floor at the kanban wall. Its local geometry
 * lives in board.ts; the boardRoot entity carries the BOARD_ISLAND_Z offset,
 * exactly as the office interior does.
 */
export const BOARD_INTERIOR: InteriorDef = {
  plotId: 'P1',
  spawn: { x: 0, z: BOARD_ISLAND_Z + (BOARD_HALF_Z - 1.5), yaw: 0 },
  exit: streetExitFor(PLOTS.find((p) => p.id === 'P1')!),
  exitThreshold: {
    plotId: 'P1',
    minX: -1.2,
    maxX: 1.2,
    minZ: BOARD_ISLAND_Z + (BOARD_HALF_Z - 1.4),
    maxZ: BOARD_ISLAND_Z + (BOARD_HALF_Z + 0.4),
  },
}

/**
 * The OPS interior gets its own island slot, clear of the board's -120 slot so
 * no two shells + backdrops overlap. Office spans ±18 m around -1000; ops
 * spans ±10 m around -1240.
 */
export const OPS_ISLAND_Z = INTERIOR_ISLAND_Z - 240

/** The OPS interior (plot P2) — queue lane, daemon obelisk, pool board, approvals. */
export const OPS_INTERIOR: InteriorDef = {
  plotId: 'P2',
  spawn: { x: 0, z: OPS_ISLAND_Z + (OPS_HALF_Z - 1.5), yaw: 0 },
  exit: streetExitFor(PLOTS.find((p) => p.id === 'P2')!),
  exitThreshold: {
    plotId: 'P2',
    minX: -1.2,
    maxX: 1.2,
    minZ: OPS_ISLAND_Z + (OPS_HALF_Z - 1.4),
    maxZ: OPS_ISLAND_Z + (OPS_HALF_Z + 0.4),
  },
}

/** The LIBRARY interior (plot P7) — bookcase stacks, red-string knowledge graph. */
export const LIBRARY_ISLAND_Z = INTERIOR_ISLAND_Z - 360

export const LIBRARY_INTERIOR: InteriorDef = {
  plotId: 'P7',
  spawn: { x: 0, z: LIBRARY_ISLAND_Z + (LIBRARY_HALF_Z - 1.5), yaw: 0 },
  exit: streetExitFor(PLOTS.find((p) => p.id === 'P7')!),
  exitThreshold: {
    plotId: 'P7',
    minX: -1.2, maxX: 1.2,
    minZ: LIBRARY_ISLAND_Z + (LIBRARY_HALF_Z - 1.4),
    maxZ: LIBRARY_ISLAND_Z + (LIBRARY_HALF_Z + 0.4),
  },
}

/** Specialist campus rooms continue south in 120 m slots, clear of backdrops. */
export const EXCHANGE_ISLAND_Z = INTERIOR_ISLAND_Z - 480
export const WORKSHOP_ISLAND_Z = INTERIOR_ISLAND_Z - 600
export const CAFE_ISLAND_Z = INTERIOR_ISLAND_Z - 720
export const DATACENTRE_ISLAND_Z = INTERIOR_ISLAND_Z - 840

function campusInterior(
  plotId: 'P4' | 'P5' | 'P6' | 'P8',
  islandZ: number,
  halfZ: number,
): InteriorDef {
  return {
    plotId,
    spawn: { x: 0, z: islandZ + (halfZ - 1.5), yaw: 0 },
    exit: streetExitFor(PLOTS.find((plot) => plot.id === plotId)!),
    exitThreshold: {
      plotId,
      minX: -1.2,
      maxX: 1.2,
      minZ: islandZ + (halfZ - 1.4),
      maxZ: islandZ + (halfZ + 0.4),
    },
  }
}

export const EXCHANGE_INTERIOR = campusInterior('P4', EXCHANGE_ISLAND_Z, CAMPUS_ROOMS.exchange.halfZ)
export const WORKSHOP_INTERIOR = campusInterior('P5', WORKSHOP_ISLAND_Z, CAMPUS_ROOMS.workshop.halfZ)
export const CAFE_INTERIOR = campusInterior('P6', CAFE_ISLAND_Z, CAMPUS_ROOMS.cafe.halfZ)
export const DATACENTRE_INTERIOR = campusInterior('P8', DATACENTRE_ISLAND_Z, CAMPUS_ROOMS.datacentre.halfZ)

export const INTERIORS: readonly InteriorDef[] = [
  OFFICE_INTERIOR,
  BOARD_INTERIOR,
  OPS_INTERIOR,
  LIBRARY_INTERIOR,
  EXCHANGE_INTERIOR,
  WORKSHOP_INTERIOR,
  CAFE_INTERIOR,
  DATACENTRE_INTERIOR,
]

/** Interior for a plot, if one has been built yet. */
export function interiorFor(plotId: string): InteriorDef | null {
  return INTERIORS.find((i) => i.plotId === plotId) ?? null
}
